import React from "react";
import { detectPackage } from "@/lib/utils";
import SortHeader, { useSort } from "@/components/SortHeader";
import OfficialPayrollRow from "@/components/OfficialPayrollRow";

/**
 * Vue "Résumé / Paie" d'un événement : statistiques, répartition forfaits,
 * et tableau de paie ventilée par officiel.
 * Extracted from pages/EventDetail.jsx (refactor Feb 27, 2026).
 */
export default function SummaryView({ event, officials, grades, settings, extras = [], onExtrasRefresh }) {
  const playable = (event.games || []).filter((g) => !g.is_break);
  const packages = settings.packages || {};

  const packageCounts = { A: 0, B: 0, C: 0, D: 0 };
  let totalCharge = 0;
  let baseTotalPay = 0;
  const officialStats = {};

  playable.forEach((g) => {
    const pkg = detectPackage(g);
    if (pkg) {
      packageCounts[pkg]++;
      totalCharge += Number(packages[pkg]?.rate || 0);
    }
    [
      [g.referee1_id, "ref"],
      [g.referee2_id, "ref"],
      [g.scorer_id, "sc"],
    ].forEach(([id, type]) => {
      if (!id) return;
      const off = officials.find((o) => o.id === id);
      if (!off) return;
      const grade =
        type === "ref"
          ? grades.find((gr) => gr.id === off.referee_grade_id && gr.type === "referee")
          : grades.find((gr) => gr.id === off.scorer_grade_id && gr.type === "scorer");
      const rate = Number(grade?.rate || 0);
      baseTotalPay += rate;
      if (!officialStats[id]) officialStats[id] = { off, games: 0, basePay: 0, extras: [], deductions: [] };
      officialStats[id].games++;
      officialStats[id].basePay += rate;
    });
  });

  // Attach extras / deductions per official (also create rows for officials with extras only)
  (extras || []).forEach((x) => {
    let entry = officialStats[x.official_id];
    if (!entry) {
      const off = officials.find((o) => o.id === x.official_id);
      if (!off) return;
      entry = officialStats[x.official_id] = { off, games: 0, basePay: 0, extras: [], deductions: [] };
    }
    if (x.kind === "extra") entry.extras.push(x);
    else if (x.kind === "deduction") entry.deductions.push(x);
  });

  // Inclure aussi tous les officiels actifs même sans assignation ni extras,
  // pour que le gestionnaire n'oublie pas de céduler certains arbitres/marqueurs.
  (officials || []).forEach((o) => {
    if (o.status && o.status !== "active") return; // ignorer pending/blocked
    if (!officialStats[o.id]) {
      officialStats[o.id] = { off: o, games: 0, basePay: 0, extras: [], deductions: [] };
    }
  });

  const sumExtras = (list) => list.reduce((s, x) => s + Number(x.amount || 0), 0);
  const totalExtrasBillable = (extras || []).filter((x) => x.kind === "extra").reduce((s, x) => s + Number(x.amount || 0), 0);
  const totalDeductions = (extras || []).filter((x) => x.kind === "deduction").reduce((s, x) => s + Number(x.amount || 0), 0);
  const totalPay = baseTotalPay + totalExtrasBillable - totalDeductions;
  totalCharge = totalCharge + totalExtrasBillable;

  const officialList = Object.values(officialStats);

  // Excel-like column sort
  const { sortKey, sortDir, cycleSort } = useSort(null);
  const sortedOfficialList = [...officialList].sort((a, b) => {
    const aExSum = sumExtras(a.extras);
    const aDedSum = sumExtras(a.deductions);
    const aNet = a.basePay + aExSum - aDedSum;
    const bExSum = sumExtras(b.extras);
    const bDedSum = sumExtras(b.deductions);
    const bNet = b.basePay + bExSum - bDedSum;
    if (!sortKey) {
      // Default: officiels non cédulés en bas, puis par net desc
      if (a.games === 0 && b.games !== 0) return 1;
      if (a.games !== 0 && b.games === 0) return -1;
      return bNet - aNet;
    }
    let va, vb;
    switch (sortKey) {
      case "official":
        va = `${a.off.first_name} ${a.off.last_name}`.toLowerCase();
        vb = `${b.off.first_name} ${b.off.last_name}`.toLowerCase();
        break;
      case "games": va = a.games; vb = b.games; break;
      case "basePay": va = a.basePay; vb = b.basePay; break;
      case "exSum": va = aExSum; vb = bExSum; break;
      case "dedSum": va = aDedSum; vb = bDedSum; break;
      case "net": va = aNet; vb = bNet; break;
      default: return 0;
    }
    if (typeof va === "string") {
      const cmp = va.localeCompare(vb, "fr", { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    }
    return sortDir === "asc" ? va - vb : vb - va;
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="event-summary">
      <Stat label="Parties planifiées" value={playable.length} />
      <Stat label="Total à payer (officiels)" value={`${totalPay.toFixed(2)} $`} />
      <Stat label="Total à facturer (client)" value={`${totalCharge.toFixed(2)} $`} accent />
      <Stat label="Marge brute" value={`${(totalCharge - totalPay).toFixed(2)} $`} />

      <div className="card-flat lg:col-span-2">
        <div className="label mb-3">Répartition forfaits</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["A", "B", "C", "D"].map((k) => (
            <div key={k} className="border border-black p-3">
              <div className="font-serif text-3xl tabular">{packageCounts[k]}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-1">
                Forfait {k} · {packages[k]?.label || ""}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-flat lg:col-span-2" data-testid="officials-breakdown">
        <div className="label mb-3">Officiels — paie ventilée</div>
        {(() => {
          const unassignedCount = officialList.filter((r) => r.games === 0).length;
          if (unassignedCount === 0) return null;
          return (
            <div
              className="mb-3 border border-black p-2 text-xs flex items-center gap-2"
              style={{ background: "#dbeafe" }}
              data-testid="unassigned-officials-warning"
            >
              <span className="font-bold">⚠</span>
              <span>
                <strong>{unassignedCount}</strong> officiel{unassignedCount > 1 ? "s" : ""} sans aucune assignation pour cet événement — visible{unassignedCount > 1 ? "s" : ""} en bleu ci-dessous.
              </span>
            </div>
          );
        })()}
        <p className="font-mono text-[10px] text-[var(--muted)] mb-4 leading-relaxed">
          Ajoute des <b>extras</b> (payés + facturés au client, ex: essence) ou des <b>déductions</b> (paie seulement, ex: commande de vêtements) directement sur la ligne de chaque officiel.
        </p>
        {officialList.length === 0 ? (
          <p className="text-xs text-[var(--muted)]">Aucun officiel assigné.</p>
        ) : (
          <div className="border-t-2 border-b-2 border-black overflow-x-auto">
            <table className="w-full text-xs" data-testid="officials-breakdown-table">
              <thead className="sticky-th">
                <tr className="border-b-2 border-black">
                  <SortHeader k="official" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Officiel</SortHeader>
                  <SortHeader k="games" align="right" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Parties</SortHeader>
                  <SortHeader k="basePay" align="right" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Paie base</SortHeader>
                  <SortHeader k="exSum" align="right" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Extras&nbsp;(+)</SortHeader>
                  <SortHeader k="dedSum" align="right" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Déductions&nbsp;(−)</SortHeader>
                  <SortHeader k="net" align="right" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Net</SortHeader>
                  <th className="py-2 px-2" />
                </tr>
              </thead>
              <tbody>
                {sortedOfficialList.map((row) => {
                  const exSum = sumExtras(row.extras);
                  const dedSum = sumExtras(row.deductions);
                  const net = row.basePay + exSum - dedSum;
                  return (
                    <OfficialPayrollRow
                      key={row.off.id}
                      row={row}
                      net={net}
                      exSum={exSum}
                      dedSum={dedSum}
                      eventId={event.id}
                      onChanged={onExtrasRefresh}
                    />
                  );
                })}
                <tr className="border-t-2 border-black bg-[var(--surface-2)]" data-testid="officials-breakdown-totals">
                  <td className="py-2 px-2 font-bold uppercase tracking-widest text-[10px]">Totaux</td>
                  <td className="py-2 px-2 text-right tabular font-bold">{officialList.reduce((s, r) => s + r.games, 0)}</td>
                  <td className="py-2 px-2 text-right tabular font-bold">{baseTotalPay.toFixed(2)} $</td>
                  <td className="py-2 px-2 text-right tabular font-bold">+{totalExtrasBillable.toFixed(2)} $</td>
                  <td className="py-2 px-2 text-right tabular font-bold">−{totalDeductions.toFixed(2)} $</td>
                  <td className="py-2 px-2 text-right tabular font-bold">{totalPay.toFixed(2)} $</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`border ${accent ? "border-black bg-black text-white" : "border-black bg-white"} p-5`}>
      <div className={`label ${accent ? "text-white/60" : ""}`}>{label}</div>
      <div className="font-serif text-5xl mt-1 tabular leading-none">{value}</div>
    </div>
  );
}
